import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Options } from '@angular-slider/ngx-slider';
import Map from 'ol/Map';
import View from 'ol/View';
import OSM from 'ol/source/OSM';
import { BingMaps, XYZ } from 'ol/source';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import WKT from 'ol/format/WKT';
import LineString from 'ol/geom/LineString';
import { Fill, Stroke, Style } from 'ol/style';
import { FullScreen, defaults as defaultControls } from 'ol/control';
import * as olProj from 'ol/proj';

import axios from 'src/app/Service/axios';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { MapService } from 'src/app/Service/map.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';

declare let $: any;

const BING_MAP_KEY ='Ap0AObt84NcDaUThCeWOj52ZqUHv6k4TJhjLibR-DghC-semgoj-0uPbIi8r0E4j';
const DEFAULT_CENTER: [number, number] = [51.3833554, 35.6770113];

@Component({
  selector: 'Route',
  templateUrl: './routecomponent.html',
  styleUrls: ['./RouteComponent.css'],
})
export class RouteComponent implements OnInit, OnDestroy {
  // ---------- map ----------
  map: any = null;
  baseLayers: any[] = [];
  routeSource: any;
  routeLayer: any;

  // ---------- route data ----------
  unitId = '';
  geofenceId = '';
  fullRoute: any;
  routeData: any;
  routeLength = 0;
  hoveredRoutePoint: { strTime?: string; iSpeed?: number } | null = null;
  routeTipX = 0;
  routeTipY = 0;

  // ---------- playback state ----------
  isPlaybackMode = false;
  playbackTimer: any;
  playbackIndex = 1;
  playbackStartIndex = 0;
  playbackEndIndex: any;
  pendingStartIndex = 0;
  playbackIntervalMs = Math.round(500 / 1.5);
  playbackSpeed = 5;
  readonly minSpeed = 1;
  readonly maxSpeed = 10;
  isPaused = false;
  showPlaybackControls = true;
  showArrowButton = true;
  rangeStartTime: any;
  rangeEndTime: any;

  // ---------- smooth vehicle marker ----------
  private vehicleMarkerLayer: any = null;
  private vehicleFrameId: number | null = null;
  private lastVehicleAngle = 0;
  // Smooth camera follow, paused while the user drags.
  private cameraFollowEnabled = true;

  // ---------- blue trail synced with the vehicle ----------
  private trailLayer: any = null;
  private trailFeature: any = null;

  // ---------- summary / vehicle ----------
  totalDistance: any;
  totalTime: any;
  carName!: string;
  carPlate!: string;
  currentTime!: string;
  currentSpeed!: number;
  currentDateTime!: string;
  tankerHeight = 0;
  tankerFill = 0;
  tankerVolume = 0;

  // ---------- flags ----------
  isTankerRoute = false;
  isFuelTanker = false;
  isSatelliteActive = false;
  isExportingPdf = false;

  // ---------- slider ----------
  sliderStart = 0;
  sliderEnd = 60;
  sliderOptions: Options = { floor: 0, ceil: 200 };

  access: any;
  zones: any[] = [];
  inspectionZones: any[] = [];
  geofenceData: any;

  dateForm: FormGroup = this.formBuilder.group({
    strSDate: new FormControl(''),
    strEDate: new FormControl(''),
  });

  lineForm: FormGroup = this.formBuilder.group({
    drawLine: new FormControl(true),
    strSDate: new FormControl(''),
    strEDate: new FormControl(''),
  });

  private readonly useOfflineMap: boolean = (<any>window).bOfflineMap;
  private readonly offlineMapUrl: string = (<any>window).strMapUrl;

  constructor(
    private notifyService: NotificationService,
    private otherService: OtherService,
    private route: ActivatedRoute,
    private mapService: MapService,
    private location: Location,
    private guardService: GurdServiceService,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.access = JSON.parse(
      this.guardService.decrypt(localStorage.getItem('menu') as string)!,
    );

    setTimeout(() => {
      this.initMap();

      this.route.url.subscribe((segment) => {
        if (segment[0].path === 'tanker-route') this.isTankerRoute = true;

        this.route.params.subscribe((params) => {
          this.unitId = params['id'];
          this.geofenceId = params['polygon'];
        });
      });

      if (this.geofenceId) {
        this.loadGeofenceRoute();
        this.drawGeofencePolygon();
      } else {
        this.loadRoute(
          this.readStored('reportStime'),
          this.readStored('reportEtime'),
        );
      }
    }, 1000);

    setTimeout(() => this.attachMapInteractions(), 1500);
  }

  ngOnDestroy(): void {
    clearInterval(this.playbackTimer);
    this.cancelVehicleTween();
    if (this.map) this.map.getView().cancelAnimations();
  }

  // ================= helpers =================

  private readStored(key: string): string {
    return this.guardService.decrypt(window.localStorage.getItem(key)!);
  }

  private buildAuthModel() {
    return {
      iSystemUser: this.guardService.decrypt(localStorage.getItem('ID')!),
      strSession: this.guardService.decrypt(localStorage.getItem('Session')!),
    };
  }

  private applyRoutePoints(points: any[]) {
    this.mapService.drawRouteFull('fullRoute', this.map, points, 'Hello');
    this.fitMapToPoints(points);
    this.fullRoute = points;
    this.routeLength = points.length;
    this.playbackEndIndex = points.length;
    this.rangeStartTime = points[0].strTime;
    this.rangeEndTime = points[this.routeLength - 1].strTime;
    this.sliderOptions.ceil = this.routeLength - 1;
    this.sliderEnd = this.routeLength - 1;
  }

  private clearRoute() {
    for (const id of ['fullRoute', 'fullRoute_start', 'fullRoute_end']) {
      this.mapService.removeMarkerById(id, this.map);
    }
    this.routeData = null;
    this.fullRoute = [];
    this.routeLength = 0;
    this.totalDistance = null;
    this.totalTime = null;
  }

  private async fetchRouteSummary(model: any) {
    const response = await axios.post('GetDistanceAndTime', model);
    const summary = this.parseResponse(response.data);
    this.totalTime = summary[0].strTotalTime;
    this.totalDistance = summary[0].fTotalDistance;
    this.carName = this.readStored('carName');
    this.carPlate = this.readStored('plate');
  }

  private clearPlaybackLayers() {
    this.hoveredRoutePoint = null;
    this.mapService.removeLayerByName('route_start', this.map);
    this.mapService.removeLayerByName('route_end', this.map);
    this.mapService.removeLayerByName('route_point', this.map);
    this.mapService.removeLayerByName('route_line', this.map);
    this.mapService.removeLayerByName('ArrowName', this.map);
  }

  private redrawStoredZones() {
    this.zones.forEach(zone => this.drawZonePolygon(zone));
    this.inspectionZones.forEach(zone => this.drawZonePolygon(zone));
  }

  private parseResponse(data: any): any {
    if (typeof data !== 'string') return data;
    try { return JSON.parse(data); } catch { return data; }
  }

  private validRoutePoints(data: any): any[] {
    if (!Array.isArray(data)) throw new Error('پاسخ مسیر از سرور معتبر نیست');
    return data.filter(point => {
      const lat = Number(point?.fLat);
      const lon = Number(point?.fLon);
      return point?.fLat != null && point?.fLon != null &&
        Number.isFinite(lat) && Number.isFinite(lon) &&
        lat !== 0 && lon !== 0 && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
    });
  }

  private buildZoneLayer(
    ring: number[][],
    strokeColor: string,
    fillColor: string,
    lineDash?: number[],
  ) {
    const polygon = new Polygon([ring]);
    polygon.transform('EPSG:4326', 'EPSG:3857');
    const source = new VectorSource();
    source.addFeature(new Feature(polygon));
    return new VectorLayer({
      source,
      name: 'polygon',
      style: new Style({
        stroke: new Stroke({ color: strokeColor, width: 4, lineDash }),
        fill: new Fill({ color: fillColor }),
      }),
    } as any);
  }

  // ================= map setup =================

  initMap() {
    this.routeSource = new VectorSource({ wrapX: false });
    this.routeLayer = new VectorLayer({ source: this.routeSource });

    if (this.useOfflineMap) {
      this.baseLayers.push(
        new TileLayer({
          source: new XYZ({
            url: this.offlineMapUrl,
            attributions: 'Map data © RayanAndishehNasr',
          }),
        }),
      );
    } else {
      this.baseLayers.push(
        new TileLayer({
          visible: false,
          preload: Infinity,
          source: new BingMaps({
            key: BING_MAP_KEY,
            imagerySet: 'AerialWithLabelsOnDemand',
          }),
        }),
        new TileLayer({ source: new OSM({}) }),
        this.routeLayer,
      );
    }

    this.map = new Map({
      controls: defaultControls().extend([new FullScreen()]),
      layers: this.baseLayers,
      target: 'test',
      view: new View({
        center: olProj.fromLonLat(DEFAULT_CENTER),
        zoom: 13,
      }),
    });
  }

  private attachMapInteractions() {
    this.map.on('pointermove', (event: any) => {
      this.hoveredRoutePoint = event.dragging ? null : this.map.forEachFeatureAtPixel(
        event.pixel,
        (feature: any) => feature.get('data')?.item,
        { layerFilter: (layer: any) => layer.get('name') === 'ArrowName', hitTolerance: 3 },
      ) ?? null;
      if (!this.hoveredRoutePoint) return;
      this.routeTipX = Math.max(8, Math.min(event.pixel[0] + 12, this.map.getSize()[0] - 188));
      this.routeTipY = Math.max(8, event.pixel[1] - 84);
    });
    this.map.on('movestart', () => this.hoveredRoutePoint = null);

    this.map.on('pointerdrag', () => {
      if (!this.isPlaybackMode) return;
      this.cameraFollowEnabled = false;
      this.map.getView().cancelAnimations();
    });

    for (const key of ['zone1', 'zone2', 'inspectionZones']) {
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      try {
        const encrypted = key === 'inspectionZones' ? stored : JSON.parse(stored);
        const value = this.parseResponse(this.guardService.decrypt(encrypted));
        if (key === 'inspectionZones') {
          if (Array.isArray(value)) this.inspectionZones = value;
        } else {
          this.zones.push(value);
        }
      } catch (error) {
        console.warn('اطلاعات محدوده قابل خواندن نیست:', key, error);
      } finally {
        localStorage.removeItem(key);
      }
    }
    this.redrawStoredZones();
  }

  fitMapToPoints(points: any[] = this.fullRoute) {
    if (!points || points.length < 1) return;

    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;
    for (const p of points) {
      if (p.fLat > maxLat) maxLat = p.fLat;
      if (p.fLat < minLat) minLat = p.fLat;
      if (p.fLon > maxLon) maxLon = p.fLon;
      if (p.fLon < minLon) minLon = p.fLon;
    }

    const cornerA = this.mapService.transformCoordinates2([maxLon, maxLat]);
    const cornerB = this.mapService.transformCoordinates2([minLon, minLat]);
    this.map.getView().fit([
      Math.min(cornerA[0], cornerB[0]) - 2000,
      Math.min(cornerA[1], cornerB[1]) - 2000,
      Math.max(cornerA[0], cornerB[0]) + 2000,
      Math.max(cornerA[1], cornerB[1]) + 2000,
    ]);
  }

  toggleBaseMap() {
    if (this.baseLayers.length < 2) return;
    this.baseLayers[1].setVisible(this.isSatelliteActive);
    this.baseLayers[0].setVisible(!this.isSatelliteActive);
    this.isSatelliteActive = !this.isSatelliteActive;
  }

  // ================= route loading =================

  loadGeofenceRoute = async () => {
    this.clearRoute();
    const model = {
      strUnitID: this.unitId,
      iGeoFance: this.geofenceId,
      strDate: this.readStored('RouteDate'),
      ...this.buildAuthModel(),
    };

    try {
      const response = await axios.post('GetAllMobileHistoryInGeoFence', model);
      this.routeData = this.parseResponse(response.data);
      const points = this.validRoutePoints(this.routeData);
      if (!points.length) {
        this.notifyService.showInfo('موقعیت معتبری برای نمایش مسیر موجود نیست', '');
        return;
      }
      this.applyRoutePoints(points);
      await this.fetchRouteSummary({
        strUnitID: this.unitId,
        iGeoFance: this.geofenceId,
        strSDate: this.readStored('RouteDate'),
        strEDate: this.readStored('RouteDate'),
        strSTime: '00:00',
        strETime: '23:59',
      });
    } catch (error) {
      console.error('خطا در دریافت مسیر محدوده:', error);
      this.notifyService.showError('دریافت مسیر محدوده با خطا مواجه شد');
    }
  };

  loadRoute = async (startTime: string, endTime: string) => {
    this.clearRoute();
    const unitId = String(this.unitId);
    const model = {
      ...this.buildAuthModel(),
      strUnitID: unitId.replace(/[!*]/g, ''),
      strDate: this.readStored('RouteDate'),
      strSDate: this.readStored('RouteDate'),
      strEDate: this.readStored('RouteDate'),
      strSTime: startTime,
      strETime: endTime,
    };

    this.dateForm.patchValue(
      { strSDate: model.strSDate, strEDate: model.strEDate },
      { emitEvent: false },
    );

    this.otherService.Showspinner();

    let url = this.isTankerRoute
      ? 'Tanker_GetAllMobileHistory'
      : unitId.includes('!')
        ? 'Tanker_Discharge_Route'
        : 'GetAllMobileHistory';

    if (unitId.includes('*')) {
      url = 'FuelTanker_GetAllMobileHistory';
      model.strSDate = this.readStored('RouteSDate');
      model.strEDate = this.readStored('RouteEDate');
      this.lineForm.patchValue({
        strSDate: model.strSDate,
        strEDate: model.strEDate,
      });
      model.strUnitID = unitId.replace(/\*/g, '');
      this.isFuelTanker = true;
      this.isTankerRoute = false;
    }

    try {
      const response = await axios.post(url, model);

      if (!response.data || response.data.length < 1 || response.data === '[]') {
        this.notifyService.showInfo('داده ای برای نمایش موجود نیست', '');
        return;
      }

      this.routeData = this.parseResponse(response.data);
      const points = this.validRoutePoints(this.routeData);
      if (!points.length) {
        this.notifyService.showInfo('موقعیت معتبری برای نمایش مسیر موجود نیست', '');
        return;
      }
      this.applyRoutePoints(points);
      await this.fetchRouteSummary(model);
    } catch (error) {
      console.error('خطا در دریافت مسیر:', error);

      let message = 'خطا در دریافت مسیر خودرو. لطفاً اتصال خود را بررسی کرده و دوباره تلاش کنید.';
      if (url === 'Tanker_GetAllMobileHistory') {
        message =
          '.اتصال به سامانه تانکر برقرار نشد. لطفاً وضعیت شبکه را بررسی کنید یا با پشتیبانی تماس بگیرید.';
        this.isTankerRoute = false;
        this.isFuelTanker = false;
        this.routeData = null;
        this.fullRoute = [];
        this.routeLength = 0;
      }
      this.notifyService.showError(message);
    } finally {
      this.otherService.nonespinner();
    }
  };

  reloadRoute() {
    if (this.geofenceId) {
      this.loadGeofenceRoute();
      this.drawGeofencePolygon();
    } else {
      this.loadRoute(this.readStored('reportStime'), this.readStored('reportEtime'));
    }
  }

  // ================= playback =================

  startPlayback(fromIndex: number) {
    this.hoveredRoutePoint = null;
    this.mapService.removeMarkerById('fullRoute', this.map);
    this.mapService.removeMarkerById('fullRoute_start', this.map);
    this.mapService.removeMarkerById('fullRoute_end', this.map);
    this.mapService.removeLayerByName('ArrowName', this.map);
    this.mapService.removeLayerByName('polygon', this.map);

    this.isPlaybackMode = true;
    this.cameraFollowEnabled = true;
    this.redrawStoredZones();

    if (!this.vehicleMarkerLayer) {
      this.ensureVehicleMarker(
        this.fullRoute[fromIndex] ?? this.fullRoute[0],
      );
    }

    this.playbackTimer = setInterval(() => {
      this.playbackIndex += 1;
      const frames = this.fullRoute.slice(fromIndex, this.playbackIndex);
      this.clearPlaybackLayers();

      const drawTrail = this.lineForm.get('drawLine')?.value;
      this.mapService.drawRouteAnimate(
        'AnimatRoute',
        this.map,
        frames,
        'Hello',
        false,
        false,
        false, // Do not let the service draw the line; our own synced trail handles it.
        true, // No abrupt service centering; the camera is followed smoothly by us.
      );
      this.mapService.removeLayerByName('route_end', this.map);

      const doneFrames = frames.length > 1 ? frames.slice(0, -1) : frames;
      doneFrames.forEach((point: any, i: number) => {
        if (i % 2 !== 0 || i <= 1) return;
        const prev = doneFrames[i - 1];
        if (!prev || prev.fLon === 0 || prev.fLat === 0) return;
        const arrow = this.mapService.craeteArrow(
          [prev.fLon, prev.fLat],
          '../../../assets/Img/162.png',
          'Arrow',
          'ArrowName',
          { item: prev },
          prev.iAngle,
          drawTrail,
        );
        if (drawTrail && arrow) this.map.addLayer(arrow);
      });
      this.showArrowButton = false;

      // Bring the vehicle marker above all layers without recreating it.
      if (this.vehicleMarkerLayer) {
        this.map.removeLayer(this.vehicleMarkerLayer);
        this.map.addLayer(this.vehicleMarkerLayer);
      }

      this.updateTrail(frames, this.getVehicleXY(), drawTrail);

      const target = frames[frames.length - 1];
      const infoPoint = frames.length >= 3 ? frames[frames.length - 2] : null;
      if (infoPoint && infoPoint.fLon !== 0 && infoPoint.fLat !== 0) {
        this.currentTime = infoPoint.strTime;
        this.currentSpeed = infoPoint.iSpeed;
        this.tankerHeight = infoPoint.iHeightPercentage;
        this.tankerFill = infoPoint.iFillPercentage;
        this.currentDateTime = `${infoPoint.strTime} - ${infoPoint.strDate}`;
        this.tankerVolume = infoPoint.fVolume;
      }

      if (!this.vehicleMarkerLayer) {
        this.ensureVehicleMarker(target);
      }
      if (this.vehicleMarkerLayer) {
        this.tweenVehicleTo(target, this.playbackIntervalMs);
      }

      if (
        this.playbackIndex === this.fullRoute.length ||
        this.playbackEndIndex < this.playbackIndex
      ) {
        clearInterval(this.playbackTimer);
        this.playbackIndex = 1;
      }
    }, this.playbackIntervalMs);
  }

  // Built once and kept for the whole playback.
  private ensureVehicleMarker(point: any) {
    this.removeVehicleMarker();
    if (!point) return;
    this.vehicleMarkerLayer = this.mapService.craeteArrow(
      [point.fLon, point.fLat],
      '../../../assets/Img/download.png',
      'Vehicle',
      'VehicleMarker',
      { item: point },
      point.iAngle,
      this.lineForm.get('drawLine')?.value,
    );
    if (!this.vehicleMarkerLayer) return;
    this.map.addLayer(this.vehicleMarkerLayer);
    this.snapVehicleTo(point);
  }

  private snapVehicleTo(point: any) {
    if (!this.vehicleMarkerLayer || !point) return;
    const feature = this.vehicleMarkerLayer.getSource().getFeatures()[0];
    if (!feature?.getGeometry()) return;
    const xy = olProj.fromLonLat([point.fLon, point.fLat]);
    feature.getGeometry().setCoordinates(xy);
    this.lastVehicleAngle = Number(point.iAngle) || 0;
    this.vehicleMarkerLayer
      .getStyle?.()
      ?.getImage?.()
      ?.setRotation((Math.PI * this.lastVehicleAngle) / 180);
  }

  private tweenVehicleTo(target: any, durationMs: number) {
    this.cancelVehicleTween();
    if (!this.vehicleMarkerLayer || !target) return;
    const feature = this.vehicleMarkerLayer.getSource().getFeatures()[0];
    if (!feature?.getGeometry()) return;

    const startXY: number[] = feature
      .getGeometry()
      .getCoordinates()
      .slice();
    const endXY = olProj.fromLonLat([target.fLon, target.fLat]);
    const startAngle = this.lastVehicleAngle;
    const targetAngle = Number(target.iAngle) || 0;
    // Shortest rotation path.
    const deltaAngle = ((targetAngle - startAngle + 540) % 360) - 180;
    const styleImage = this.vehicleMarkerLayer.getStyle?.()?.getImage?.();
    const duration = Math.max(durationMs, 1);
    const t0 = performance.now();

    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      const x = startXY[0] + (endXY[0] - startXY[0]) * t;
      const y = startXY[1] + (endXY[1] - startXY[1]) * t;
      feature.getGeometry().setCoordinates([x, y]);
      // Keep the trail tip exactly on the vehicle without outrunning the line.
      this.updateTrailHead([x, y]);
      this.lastVehicleAngle = startAngle + deltaAngle * t;
      if (styleImage) {
        styleImage.setRotation((Math.PI * this.lastVehicleAngle) / 180);
      }
      // Slide the camera smoothly with the vehicle, frame by frame, with no animation queue.
      if (this.cameraFollowEnabled) {
        const view = this.map.getView();
        if (!view.getAnimating() && !view.getInteracting()) {
          view.setCenter([x, y]);
        }
      }
      this.vehicleFrameId =
        t < 1 && this.isPlaybackMode ? requestAnimationFrame(step) : null;
    };
    this.vehicleFrameId = requestAnimationFrame(step);
  }

  private cancelVehicleTween() {
    if (this.vehicleFrameId !== null) {
      cancelAnimationFrame(this.vehicleFrameId);
      this.vehicleFrameId = null;
    }
  }

  private removeVehicleMarker() {
    this.cancelVehicleTween();
    if (this.vehicleMarkerLayer) {
      this.map.removeLayer(this.vehicleMarkerLayer);
      this.vehicleMarkerLayer = null;
    }
  }

  private getVehicleXY(): number[] | null {
    try {
      const feature = this.vehicleMarkerLayer?.getSource().getFeatures()[0];
      const coords = feature?.getGeometry()?.getCoordinates();
      return coords ? coords.slice() : null;
    } catch {
      return null;
    }
  }

  // Persistent trail layer, created once.
  private ensureTrailLayer() {
    if (this.trailLayer || !this.map) return;
    this.trailFeature = new Feature({ geometry: new LineString([]) });
    this.trailFeature.setStyle(
      new Style({ stroke: new Stroke({ color: '#0000ff', width: 3 }) }),
    );
    const source = new VectorSource({ features: [this.trailFeature] });
    this.trailLayer = new VectorLayer({ source } as any);
    this.trailLayer.set('name', 'route_line_smooth');
    // Stays under the vehicle marker, which is re-added on top on every tick.
    this.map.addLayer(this.trailLayer);
  }

  private removeTrailLayer() {
    if (this.trailLayer && this.map) {
      try {
        this.map.removeLayer(this.trailLayer);
      } catch {
        // Ignore.
      }
    }
    this.trailLayer = null;
    this.trailFeature = null;
  }

  private updateTrail(
    frames: any[],
    carXY: number[] | null,
    drawTrail: boolean,
  ) {
    if (!drawTrail) {
      this.removeTrailLayer();
      return;
    }
    this.ensureTrailLayer();
    if (!this.trailFeature) return;
    const done = frames.length > 1 ? frames.slice(0, -1) : frames;
    const coords: number[][] = done.map((p: any) =>
      olProj.fromLonLat([p.fLon, p.fLat]),
    );
    if (carXY) coords.push(carXY);
    this.trailFeature.getGeometry().setCoordinates(coords);
  }

  private updateTrailHead(xy: number[]) {
    if (!this.trailFeature) return;
    const geom = this.trailFeature.getGeometry();
    const coords = geom.getCoordinates();
    if (!coords.length) return;
    coords[coords.length - 1] = xy;
    geom.setCoordinates(coords);
  }

  replayPlayback() {
    this.clearPlaybackLayers();
    this.playbackIndex = this.playbackStartIndex;
    clearInterval(this.playbackTimer);
    // Drop the marker from the end so it is rebuilt at the start with no long slide.
    this.removeVehicleMarker();
    this.removeTrailLayer();
    this.startPlayback(this.playbackStartIndex);
    this.showPlaybackControls = !this.showPlaybackControls;
  }

  stopPlayback() {
    this.clearPlaybackLayers();
    this.removeVehicleMarker();
    this.removeTrailLayer();
    if (this.map) this.map.getView().cancelAnimations();
    this.redrawStoredZones();
    this.reloadRoute();

    this.playbackIndex = 1;
    clearInterval(this.playbackTimer);
    this.isPlaybackMode = !this.isPlaybackMode;
    this.playbackEndIndex = null;
    this.playbackStartIndex = 0;
    this.showArrowButton = !this.showArrowButton;
    this.sliderStart = 0;
    this.playbackIntervalMs = Math.round(500 / 1.5);
    this.playbackSpeed = 5;
    this.isPaused = false;
  }

  pausePlayback() {
    this.isPaused = true;
    clearInterval(this.playbackTimer);
    // Freeze the marker mid-move so it does not jump when resumed.
    this.cancelVehicleTween();
  }

  resumePlayback() {
    this.isPaused = false;
    this.startPlayback(this.playbackStartIndex);
  }

  increaseSpeed() {
    this.setSpeed(this.playbackSpeed + 1);
  }

  decreaseSpeed() {
    this.setSpeed(this.playbackSpeed - 1);
  }

  private setSpeed(next: number) {
    next = Math.min(this.maxSpeed, Math.max(this.minSpeed, Math.round(next)));
    if (next === this.playbackSpeed) return;
    this.playbackSpeed = next;
    this.isPaused = false;
    // Higher speed means a shorter interval between frames.
    this.playbackIntervalMs = Math.round(2500 / (this.playbackSpeed * 1.5));
    clearInterval(this.playbackTimer);
    this.startPlayback(this.playbackStartIndex);
  }

  onRangeStartChange(index: number) {
    this.rangeStartTime = this.fullRoute[index].strTime;
    this.pendingStartIndex = index;
  }

  onRangeEndChange(index: number) {
    this.rangeEndTime = this.fullRoute[index].strTime;
    this.showPlaybackControls = false;
    this.playbackEndIndex = index;
    this.playbackStartIndex = this.pendingStartIndex;
  }

  // ================= direction arrows =================

  showDirectionArrows() {
    this.fullRoute.forEach((point: any, i: number) => {
      if (i % 2 !== 0 || point.fLon === 0 || point.fLat === 0) return;
      const marker = this.mapService.craeteArrow(
        [point.fLon, point.fLat],
        '../../../assets/Img/16.png',
        'Arrow',
        'ArrowName',
        { item: point },
        point.iAngle,
      );
      this.map.addLayer(marker);
    });
    this.showArrowButton = false;
  }

  hideDirectionArrows() {
    this.hoveredRoutePoint = null;
    this.mapService.removeMarkerById_New('Arrow', this.map);
    this.showArrowButton = true;
  }

  // ================= zones =================

  drawGeofencePolygon = async () => {
    try {
    if (!this.geofenceData) {
      const model = {
        iGeoFence: this.geofenceId,
        strSession: `${this.guardService.decrypt(localStorage.getItem('Session')!)}`,
        iSystemUser: this.guardService.decrypt(
          window.localStorage.getItem('ID')!,
        ),
      };
      const response = await axios.post('Radyab_GeoAFence', model);
      this.geofenceData = this.parseResponse(response.data);
    }

    if (Array.isArray(this.geofenceData?.lstPoints) && this.geofenceData.lstPoints.length) {
      const ring = this.geofenceData.lstPoints.map((p: any) => [
        p.fLon,
        p.fLat,
      ]);
      this.map.addLayer(
        this.buildZoneLayer(ring, 'red', 'rgba(0, 0, 255, 0)', [
          10, 20, 10, 20,
        ]),
      );
      if (!this.routeLength) this.fitMapToPoints(this.geofenceData.lstPoints);
    }
    } catch (error) {
      console.error('خطا در دریافت محدوده:', error);
      this.notifyService.showError('دریافت محدوده با خطا مواجه شد');
    }
  };

  drawZonePolygon(data: any) {
    if (typeof data === 'string') {
      try {
        const geometry = new WKT().readGeometry(data);
        if (!(geometry instanceof Polygon) || geometry.getCoordinates().length === 0) return;
        const ring = geometry.getCoordinates()[0];
        if (ring?.length) this.map.addLayer(this.buildZoneLayer(ring, 'rgba(242, 10, 10, 0.5)', 'rgba(242, 10, 10, 0.2)'));
      } catch { console.warn('قالب محدوده معتبر نیست'); }
      return;
    }
    if (!Array.isArray(data?.lstPoints) || !data.lstPoints.length) return;
    const ring = data.lstPoints.map((p: any) => [p.fLon, p.fLat]);
    this.map.addLayer(
      this.buildZoneLayer(ring, 'rgba(242, 10, 10, 0.5)', 'rgba(242, 10, 10, 0.2)'),
    );
  }

  // ================= misc actions =================

  async recalculateDistance() {
    $.confirm({
      title: 'آیا از محاسبه نرم افزاری فاصله ها اطمینان دارید ؟',
      content: 'این عملیات ممکن است تا یک دقیقه طول بکشد',
      type: 'red',
      buttons: {
        ok: {
          text: 'تایید',
          btnClass: 'btn-success',
          keys: ['enter'],
          action: async () => {
            const date = this.readStored('RouteDate');
            this.otherService.Showspinner();
            try {
              await axios.post('ReCalDistance4ADate', {
                ...this.buildAuthModel(),
                strUnitID: this.readStored('UnitID'),
                strSDate: date,
                strEDate: date,
              });
              await this.fetchRouteSummary({
                strUnitID: this.unitId,
                iGeoFance: this.geofenceId,
                strSDate: date,
                strEDate: date,
                strSTime: '00:00',
                strETime: '23:59',
              });
              this.notifyService.showSuccess('به روز رسانی با موفقیت انجام شد');
            } finally {
              this.otherService.nonespinner();
            }
          },
        },
        cancel: { text: 'لغو' },
      },
    });
  }

  exportMapPdf() {
    this.isExportingPdf = true;
    this.mapService.getPdf(this.map);
    setTimeout(() => {
      this.isExportingPdf = false;
    }, 2000);
  }

  formatPlate(plate: string | null | undefined): string {
    if (!plate) return '';
    const parts = plate.includes(',') ? plate.split(',') : plate.split(' ');
    return `ایران ${parts[4]} | ${parts[2]} ${parts[1]} ${parts[0]}`;
  }

  navigateBack() {
    this.location.back();
  }

  openGuide() {
    this.otherService.guid(1);
  }

  onRouteDateSelect(event: {
    gregorian: string;
    shamsi: string;
    timestamp: number;
  }) {
    try {
      const raw = window.localStorage.getItem('RouteDate');
      const prevDate = raw ? this.guardService.decrypt(raw) : null;
      if (prevDate && event?.shamsi && prevDate === event.shamsi) return;
    } catch {
      // Keep the previous behavior when the stored date cannot be read.
    }
    const encrypted = this.guardService.encrypt(event.shamsi);
    window.localStorage.setItem('RouteDate', encrypted);
    window.localStorage.setItem('RouteSDate', encrypted);
    window.localStorage.setItem('RouteEDate', encrypted);
    window.location.reload();
  }

}
